/**
 * One tab rings, however many are open.
 *
 * The to-do reminders (batch 3) are polled by every open Jarvis tab, and
 * every tab sees the same reminder fall due in the same thirty seconds.
 * Three tabs open is three chimes and three desktop notifications for one
 * "ring Rita back" - which is how reminders get switched off. So before a tab
 * makes a noise about something it CLAIMS it, and only the tab that wins the
 * claim rings. Every tab still shows the card: what is on screen is each
 * tab's own business, the noise is the browser's.
 *
 * THE CLAIM is a line in localStorage - "<key> was rung by tab X, good until
 * T" - and the race between two tabs reading it at once is closed with the
 * Web Locks API where there is one: an exclusive lock held only for the
 * read-and-write, so the second tab reads the first one's claim. Every
 * current browser has it. Where there is not, the fallback writes its own
 * name, waits a beat and reads it back: whoever's name is there won, and
 * at worst two tabs ring once each, which is the old behaviour rather than
 * a missed reminder.
 *
 * Claims expire, so a key the server hands out again later (a snoozed
 * reminder comes back with a new fire time, and so a new key) is never
 * blocked by an old one, and the storage does not fill up.
 *
 * Nothing in here throws: blocked storage means every tab may ring, never
 * that a reminder is lost.
 */

const PREFIX = 'jarvis-rung:';
const TTL_MS = 30 * 60 * 1000;
const TAB = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const read = (key) => {
  try {
    const v = JSON.parse(localStorage.getItem(PREFIX + key) || 'null');
    return v && typeof v.until === 'number' && v.until > Date.now() ? v : null;
  } catch {
    return null;
  }
};

const write = (key) => {
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify({ by: TAB, until: Date.now() + TTL_MS }));
    return true;
  } catch {
    return false;
  }
};

/** Old claims out, so this never grows. */
const sweep = () => {
  try {
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX) && !read(k.slice(PREFIX.length))) localStorage.removeItem(k);
    }
  } catch { /* nothing to tidy */ }
};

const decide = (key) => {
  const had = read(key);
  if (had) return had.by === TAB;
  write(key);
  return true;
};

/**
 * Resolves true in exactly one tab per `key` (barring blocked storage).
 * A tab that claimed a key and asks again gets true again, so a re-render
 * cannot make the winner lose its own ring.
 */
export async function claimRing(key) {
  sweep();
  try {
    if (typeof navigator !== 'undefined' && navigator.locks && navigator.locks.request) {
      return await navigator.locks.request('jarvis-ring-once', () => decide(key));
    }
  } catch { /* fall through to the slower way */ }
  if (read(key)) return read(key).by === TAB;
  if (!write(key)) return true;
  await new Promise((r) => { setTimeout(r, 40 + Math.floor(Math.random() * 60)); });
  const now = read(key);
  return !now || now.by === TAB;
}

/** Whose tab this is, for a test to tell them apart. */
export const tabName = () => TAB;
